import type { ApiErrorResponse } from '@/entities/api-error';
import { QueryClient, type VueQueryPluginOptions } from '@tanstack/vue-query';
import { createToast, withProps } from 'mosha-vue-toastify';
import ErrorToast from '@/components/ErrorToast.vue';
import type { ApprovalRequest } from '@carvia/ros-client-types';
import type { BadRequestErrorDto } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';
import { i18n } from '@/i18n';
import { isAxiosError } from 'axios';

export const isBadRequestError = (error?: any): error is BadRequestErrorDto => {
  return (
    error?.statusCode === 400 &&
    error.error === 'Bad Request' &&
    Array.isArray(error.message) &&
    !(error.message as Array<any>).some(
      (message) => typeof message.field !== 'string' || typeof message.error !== 'string',
    )
  );
};

export const queryClient = new QueryClient();

export const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClient,
};

export interface MutationDefaultsContext {
  successMessage?: string;
  invalidateQueryKeys?: string[] | null;
  showSuccessToast?: boolean;
  catchApprovalRequests?: boolean;
}

queryClient.setDefaultOptions({
  queries: {
    retry: (failureCount, error) => {
      if (isAxiosError(error) && [401, 403].includes(error.status ?? 0)) {
        return false;
      }
      return failureCount < 3;
    },
  },
});

queryClient.setMutationDefaults([], {
  onSuccess: (
    data,
    variables,
    {
      successMessage,
      invalidateQueryKeys,
      showSuccessToast = true,
      catchApprovalRequests = false,
    }: MutationDefaultsContext = {},
  ) => {
    if (catchApprovalRequests && isApprovalRequest(data)) {
      Alert.fire({
        title: i18n.global.t('approvalRequests.approvalRequired'),
        html: i18n.global.t('approvalRequests.approvalRequiredDescription'),
      });
    } else {
      invalidateQueryKeys && queryClient.invalidateQueries({ queryKey: invalidateQueryKeys });
      showSuccessToast &&
        createToast(successMessage ?? 'Success', {
          position: 'bottom-right',
          type: 'success',
        });
    }
  },

  onError: (error) => {
    // Skip if error was already handled by a mutation-specific handler
    if ((error as any).__handled) {
      return;
    }

    const errorData = (error as ApiErrorResponse).response?.data;
    const errorMessage = isBadRequestError(errorData)
      ? errorData.message
          .map(({ field, error: fieldError }) => `${field}: ${fieldError}`)
          .join(', ')
      : (errorData?.message ?? (error as Error).message);

    createToast(
      withProps(ErrorToast, {
        message: Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage,
        error,
      }),
      {
        type: 'danger',
        position: 'bottom-right',
      },
    );
  },
});

function isApprovalRequest(a: any): a is ApprovalRequest {
  return typeof a === 'object' && 'requester' in a && 'jobData' in a && 'jobName' in a;
}
window.addEventListener('refetch-queries', () => {
  queryClient.invalidateQueries();
});
